import { observer } from "mobx-react-lite"
import { makeAutoObservable } from "mobx"
import classNames from "classnames"
import styles from "../../styles.module.scss"
import { IUser } from "@cruisen/contract"
import { useMemo } from "react"
import { useMutation } from "@tanstack/react-query"
import { getPublicApiKey } from "../../../../../../../services/firebase-functions/userSettings"
import { SocialMediaButton } from "../../components/SocialMediaButton"
import { Tooltip } from "../../../../../../atoms/Tooltip"
import Skeleton, { SkeletonTheme } from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import { colors } from "@theme/colors"
import Config from "../../../../../../../config"

class IntegrationsTabStore {
  apiKey: string
  isCopied: boolean

  constructor() {
    this.apiKey = ""
    this.isCopied = false

    makeAutoObservable(this)
  }

  setApiKey(apiKey: string) {
    this.apiKey = apiKey
  }

  setCopied(isCopied: boolean) {
    this.isCopied = isCopied
  }
}

export const IntegrationsTab = observer(({ loggedInUser }: { loggedInUser: IUser }) => {
  const store = useMemo(() => new IntegrationsTabStore(), [])

  const { mutate: revealApiKey, isPending } = useMutation({
    mutationFn: async () => {
      const apiKey = await getPublicApiKey()

      store.setApiKey(apiKey)
    },
  })

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value)
    store.setCopied(true)

    setTimeout(() => {
      store.setCopied(false)
    }, 750)
  }

  return (
    <div className={classNames(styles.accountModalBody, styles.integrations)}>
      <div className={styles.integrationDetailsContainer}>
        <div className={styles.sectionInfo}>
          <p className={styles.sectionTitle}>API key</p>
          <p className={styles.sectionDescription}>
            Use this key to integrate Cruisen with your other tools.
          </p>
        </div>
        <div className={styles.integrationDetails}>
          {isPending ? (
            <SkeletonTheme
              baseColor={colors.backgroundAdditional}
              highlightColor={colors.backgroundQuaternary}
            >
              <Skeleton containerClassName={styles.skeletonContainer} />
            </SkeletonTheme>
          ) : store.apiKey ? (
            <>
              <p
                onClick={() => copyToClipboard(store.apiKey)}
                className={styles.apiKey}
                data-tooltip-id="apiKey"
                data-tooltip-content="Copied!"
                data-tooltip-place="bottom"
              >
                {store.apiKey}
              </p>
              <Tooltip id="apiKey" isOpen={store.isCopied} place="bottom" />
            </>
          ) : (
            <p className={styles.apiKey}>•••••••••</p>
          )}
          <button
            onClick={() => {
              if (store.apiKey) {
                store.setApiKey("")
              } else {
                revealApiKey()
              }
            }}
            disabled={isPending}
            className={styles.dataRevealButton}
          >
            {store.apiKey ? "Hide API key" : "Reveal API key"}
          </button>
        </div>
      </div>
      <div className={styles.divider} />
      <div className={styles.integrationDetailsContainer}>
        <div className={styles.sectionInfo}>
          <p className={styles.sectionTitle}>Google sheet sample</p>
          <p className={styles.sectionDescription}>
            Create a copy to store your trips, profiles, and bookings.
          </p>
        </div>
        <div className={styles.agencyInfoForm}>
          <div className={styles.socialMediaWrapper}>
            <SocialMediaButton
              socialMediaName="googleSheets"
              onClick={() => {
                const url = `${Config.googleSheetsSampleUrl}`
                window.open(url, "_blank")
              }}
            />
          </div>
        </div>
      </div>
      <div className={styles.divider} />
      <zapier-workflow
        sign-up-email={loggedInUser?.email}
        sign-up-first-name={loggedInUser?.firstName}
        sign-up-last-name={loggedInUser?.lastName}
        client-id="Ey3wAEV82sNPbal6EW2A4wqCF2TyHmcqtWJG3i82"
        theme="dark"
        intro-copy-display="show"
        guess-zap-display="hide"
        template-ids="255643695,255643697,255643698"
      />
    </div>
  )
})
